import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/format';
import Modal from './Modal';
import type { AdminRequest } from '@/mocks/adminRequests';
import type { ActivateResult } from '@/hooks/useAdminData';

type ActivationModalProps = {
  request: AdminRequest;
  managerEmail: string;
  onClose: () => void;
  onActivate: (requestId: string, code: string, manager: string) => Promise<ActivateResult>;
};

export default function ActivationModal({ request, managerEmail, onClose, onActivate }: ActivationModalProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = await onActivate(request.id, code, managerEmail);

    if (!result.ok) {
      if (result.reason === 'expired') {
        setError(t('admin.activation.errorExpired'));
      } else if (result.reason === 'used') {
        setError(t('admin.activation.errorUsed'));
      } else {
        setError(t('admin.activation.errorMismatch'));
      }
      setSuccess(false);
      return;
    }

    setError(null);
    setSuccess(true);
  };

  return (
    <Modal
      title={t('admin.activation.title')}
      subtitle={t('admin.activation.subtitle')}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 rounded-md border border-background-200 bg-background-100 p-4 text-sm">
          <div>
            <p className="text-xs text-foreground-500">{t('admin.activation.infoRef')}</p>
            <p className="mt-0.5 font-semibold text-foreground-900">{request.reference}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">{t('admin.activation.infoAmount')}</p>
            <p className="mt-0.5 font-semibold text-foreground-900">{formatCurrency(request.amount, request.currency)}</p>
          </div>
          <div className="col-span-2">
            <p className="text-xs text-foreground-500">{t('admin.activation.infoBeneficiary')}</p>
            <p className="mt-0.5 font-semibold text-foreground-900">{request.fullName}</p>
          </div>
        </div>

        {success ? (
          <div className="flex items-start gap-3 rounded-md border border-primary-200 bg-primary-50 p-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-check-line text-base"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-primary-900">
                {t('admin.activation.success')}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-2 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                {t('admin.activation.close')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.activation.codeLabel')}
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={8}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                placeholder={t('admin.activation.codePh')}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-center font-heading text-lg tracking-[0.4em] text-foreground-950 outline-none transition-colors placeholder:tracking-normal placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                {t('admin.activation.cancel')}
              </button>
              <button
                type="submit"
                disabled={!code}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground-950"
              >
                <i className="ri-shield-check-line mr-1.5"></i>
                {t('admin.activation.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}